import { useCallback, useEffect } from 'react';
import { isNil } from 'shared/src/utils';
import { useIsFocusedTab, useLogAnalyticsEvent } from 'shared/src/hooks';
import { useRetailAd } from '../queries/useRetailAd';
import { usePostRetailAdDisplayEvent } from '../queries/usePostRetailAdDisplayEvent';
import type { RetailAdInfo } from 'shared/src/api';

const getAdInfoParams = (adInfo: RetailAdInfo) => ({
  AD_ID: adInfo.ad_executable_id,
  AD_PLACEMENT_ID: adInfo.ad_placement_id,
  AD_NAME: adInfo.ad_executable_name,
  ADVERTISING_SUPPLIER: adInfo.ad_supplier,
  CREATIVE_TYPE: adInfo.creative_type,
  LINK: adInfo.creative_link,
});

export const useLogRetailAd = (adSlotId: number) => {
  const isFocusedTab = useIsFocusedTab();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { data: retailAd, status, refetch: refetchRetailAd } =
    useRetailAd(adSlotId);
  const { mutate: sendDisplayEvent } = usePostRetailAdDisplayEvent();

  // BottomTabNavigator 는 탭 전환 시 화면을 unmount 하지 않으므로 useEffect 의 mount
  // 사이클만 의존하면 2 번째 이후 진입에서 CALL_ADS 가 누락된다.
  // iOS ViewController.viewWillAppear / Android onResume 과 같이 매 focus 진입마다
  // CALL → fetch → LOAD 흐름을 보장하기 위해 isFocusedTab 전환을 트리거로 사용하고,
  // refetch Promise 를 직접 await 해서 응답 직후에만 LOAD_ADS 를 emit 한다.
  // - useEffect 한 개로 합쳐서 status/retailAd reference 변경에 의한 중복 emit 차단
  // - cache hit 으로 인한 race condition 없음 (Promise resolve = 새 fetch 결과)
  useEffect(() => {
    if (!isFocusedTab) return;

    logAnalyticsEvent({
      name: 'CALL_ADS',
      channel: ['ablylog'],
      analytics: { AD_SLOT_ID: adSlotId },
    });

    refetchRetailAd().then(({ data }) => {
      if (isNil(data)) return;

      // iOS OverviewFeature.fetchRetailAds 의 ad_info nil 분기와 일치:
      //   nil → { STATUS: -1, ADVERTISING_SUPPLIER: 'ABLY' } (AD_SLOT_ID 미포함)
      //   nil 아님 → ad_info 상세 + STATUS = ad_response_type
      const adInfo = data.ad_info;
      logAnalyticsEvent({
        name: 'LOAD_ADS',
        channel: ['ablylog'],
        analytics:
          adInfo != null
            ? {
                STATUS: data.ad_response_type,
                ...getAdInfoParams(adInfo),
              }
            : {
                STATUS: -1,
                ADVERTISING_SUPPLIER: 'ABLY',
              },
      });
    });
  }, [isFocusedTab, adSlotId, refetchRetailAd, logAnalyticsEvent]);

  const logDisplayAds = useCallback(() => {
    if (status !== 'success') return;
    if (retailAd?.ad_info == null) return;

    logAnalyticsEvent({
      name: 'DISPLAY_ADS',
      channel: ['ablylog'],
      analytics: getAdInfoParams(retailAd.ad_info),
    });

    // iOS OverviewRetailAdCell.setImage 콜백 / Android RetailAdEventStateImpl.onDisplay
    // 와 동일하게 DISPLAY 비콘을 서버로 전송 (노출 카운팅, fire-and-forget).
    sendDisplayEvent(retailAd.ad_info.ad_executable_id);
  }, [retailAd?.ad_info, status, sendDisplayEvent, logAnalyticsEvent]);

  const logClickAds = useCallback(() => {
    if (retailAd?.ad_info == null) return;

    logAnalyticsEvent({
      name: 'CLICK_ADS',
      channel: ['ablylog'],
      analytics: getAdInfoParams(retailAd.ad_info),
    });
  }, [retailAd, logAnalyticsEvent]);

  return { logDisplayAds, logClickAds };
};
